
import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { Button } from './ui/Button';
import { Lock as LockIcon, Mail, ArrowRight, AlertTriangle, Eye, EyeOff, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';

export const Auth: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false); // Toggle between Login and Register
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  useEffect(() => {
    // Check for hash errors from Supabase redirect (OAuth errors)
    const hash = window.location.hash;
    if (hash && hash.includes('error_description')) {
        const params = new URLSearchParams(hash.substring(1));
        const description = params.get('error_description')?.replace(/\+/g, ' ');
        if (description) setErrorMsg(description);
    }
  }, []);

  const handleGoogleLogin = async () => {
      setLoading(true);
      setErrorMsg(null);

      // SMART REDIRECT LOGIC
      // 1. If Localhost -> keep localhost (for dev)
      // 2. If Production -> FORCE https://lockd.in (Root)
      // We redirect to Root instead of /#/dashboard to allow Supabase to parse the hash fragment cleanly.
      // The App.tsx will automatically redirect to /dashboard once the session is detected.
      let redirectUrl = window.location.origin;
      
      const hostname = window.location.hostname;
      if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
          redirectUrl = 'https://lockd.in';
      }

      const { error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
              redirectTo: redirectUrl,
          }
      });
      if (error) {
          setErrorMsg(error.message);
          setLoading(false);
      }
      // Note: No setLoading(false) here because we are redirecting away
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg(null);
    setSuccessMsg(null);
    
    try {
        if (isSignUp) {
            // SIGN UP FLOW
            const { error } = await supabase.auth.signUp({
                email,
                password,
            });
            if (error) throw error;
            setSuccessMsg("Account created! You can now log in.");
            setIsSignUp(false); // Switch to login view
        } else {
            // SIGN IN FLOW
            const { error } = await supabase.auth.signInWithPassword({
                email,
                password,
            });
            if (error) throw error;
            // Redirect handled by App.tsx session listener
        }
    } catch (error: any) {
        setErrorMsg(error.message || "An error occurred");
    } finally {
        setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 font-sans">
        <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md w-full bg-white rounded-[32px] shadow-xl shadow-slate-200/50 border border-slate-100 p-8 sm:p-10"
        >
            <div className="flex justify-center mb-6">
                <div className="w-14 h-14 bg-slate-900 rounded-2xl flex items-center justify-center shadow-lg shadow-slate-900/20">
                    <LockIcon className="w-7 h-7 text-white fill-white/20" />
                </div>
            </div>

            <h1 className="text-2xl font-bold text-center text-slate-900 mb-2">
                {isSignUp ? "Create an account" : "Welcome back"}
            </h1>
            <p className="text-center text-slate-500 mb-8 text-sm">
                {isSignUp ? "Start gating your content today." : "Log in to manage your locks."}
            </p>

            {/* ERROR Message */}
            {errorMsg && (
                <div className="mb-6 p-3 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3 text-red-600 text-xs">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <div>{errorMsg}</div>
                </div>
            )}

            {/* SUCCESS Message */}
            {successMsg && (
                <div className="mb-6 p-3 bg-green-50 border border-green-100 rounded-xl flex items-center gap-3 text-green-600 text-xs font-medium">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <div>{successMsg}</div>
                </div>
            )}

            {/* GOOGLE LOGIN BUTTON */}
            <button 
                type="button"
                onClick={handleGoogleLogin}
                disabled={loading}
                className="w-full h-12 bg-white border border-slate-200 hover:bg-slate-50 hover:border-slate-300 text-slate-700 font-medium rounded-xl flex items-center justify-center gap-3 transition-all mb-6 focus:ring-2 focus:ring-slate-100 outline-none"
            >
                {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                ) : (
                    <>
                        {/* Standard Google SVG Icon */}
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                        </svg>
                        <span>Sign in with Google</span>
                    </>
                )}
            </button>

            <div className="flex items-center gap-4 mb-6">
                <div className="h-px flex-1 bg-slate-100"></div>
                <span className="text-xs text-slate-400 font-medium">OR</span>
                <div className="h-px flex-1 bg-slate-100"></div>
            </div>

            {/* EMAIL FORM */}
            <form onSubmit={handleEmailAuth} className="space-y-4">
                <div>
                    <label className="sr-only">Email</label>
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <Mail className="h-5 w-5 text-slate-400" />
                        </div>
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="block w-full pl-11 pr-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:bg-white transition-all text-sm font-medium"
                            placeholder="name@work.com"
                        />
                    </div>
                </div>
                
                <div>
                    <label className="sr-only">Password</label>
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <LockIcon className="h-5 w-5 text-slate-400" />
                        </div>
                        <input
                            type={showPassword ? "text" : "password"}
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            minLength={6}
                            className="block w-full pl-11 pr-10 py-3.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:bg-white transition-all text-sm font-medium"
                            placeholder="Password"
                        />
                        <button 
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
                        >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                    </div>
                </div>

                <Button type="submit" fullWidth isLoading={loading} className="h-12 text-base mt-2">
                    {isSignUp ? "Sign Up" : "Sign In"} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
            </form>
            
            <div className="mt-8 text-center">
                <button 
                    onClick={() => {
                        setIsSignUp(!isSignUp);
                        setErrorMsg(null);
                        setSuccessMsg(null);
                    }}
                    className="text-sm text-slate-500 hover:text-slate-900 font-medium transition-colors"
                >
                    {isSignUp ? "Already have an account? Sign in" : "Don't have an account? Sign up"}
                </button>
            </div>
        </motion.div>
    </div>
  );
};
